import {
  Button,
  Card,
  Tabs,
  Tooltip,
  Badge,
  Statistic,
  Row,
  Col,
  Divider,
  Space,
  Tag,
  message,
  Select,
  Alert,
} from "antd";
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Spin, Typography, Progress } from "antd";
import {
  LoadingOutlined,
  LineChartOutlined,
  PieChartOutlined,
  BarChartOutlined,
  RiseOutlined,
  PercentageOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { Award } from "lucide-react";
import { ViewResultsProps } from "../../../../models/interfaces";
import axiosInstance from "../../../../utils/axiosInstance";
import LineChart, {
  LineChartProps,
  LineData,
} from "../../../../components/LineChart";
import PieChartECharts from "../../../../components/PieChart";
import WaterFallChart from "../../../../components/WaterFallChart";
import HeatMap from "../../../../components/HeatMap";
import MultiBarLineChartECharts, {
  MultiBarLineChartProps,
} from "../../../../components/BarChart";
import GradientStackedAreaChart from "../../../../components/GradientStackedAreaChart";
import Table, { TableColumn, TableRecord } from "../../../../components/Table";
import Maximize from "../../../../components/Maximize";
import ScenarioTable from "../../../../components/ScenarioTable";
import Chat from "../../../../components/Chat/Chat";
import {
  CONTRIBUTION_PERC_API,
  MMM_INPUT_DATA,
  OPTIMIZE_SPEND_API,
  MMM_CHAT_API,
} from "../../../../constants/API";
import { getTenantId } from "../../../../utils/auth";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import MediaChannelDashboardRoi from "../../../../components/MediaChannelDashboardRoi";

interface LiftData {
  percentage: number;
  title: string;
  confidence: number;
  date: string;
  spend: number;
  sales: number;
}

type ContributionItem = {
  Variables: string;
  Contributions: number;
};

// Define multiColinearityColumn outside the component
const multiColinearityColumn: TableColumn[] = [
  { key: "variable", label: "Variable" },
  { key: "value", label: "Value", decimal: 3 },
];

// Define scenarioData outside the component
let scenarioData: any = {
  "Scenario 1": {
    variables: { "Total Budget": "", Weeks: "" },
    yearlySpendChartData: [],
    contributionChartData: [],
    labels: [],
  },
  "Scenario 2": {
    variables: { "Total Budget": "", Weeks: "" },
    yearlySpendChartData: [],
    contributionChartData: [],
    labels: [],
  },
  "Scenario 3": {
    variables: { "Total Budget": "", Weeks: "" },
    yearlySpendChartData: [],
    contributionChartData: [],
    labels: [],
  },
  "Scenario 4": {
    variables: { "Total Budget": "", Weeks: "" },
    yearlySpendChartData: [],
    contributionChartData: [],
    labels: [],
  },
  "Scenario 5": {
    variables: { "Total Budget": "", Weeks: "" },
    yearlySpendChartData: [],
    contributionChartData: [],
    labels: [],
  },
};

const ViewResults: React.FC<ViewResultsProps> = ({
  jobId: propJobId,
  setSpendVariables,
}) => {
  const { Title, Paragraph } = Typography;
  const [searchParams, setSearchParams] = useSearchParams();
  // Check if user came from next button (via URL parameter)
  const fromNextButton = searchParams.get("fromNext") === "true";
  // Get jobId from URL or props
  const jobId = searchParams.get("jobId") || propJobId;
  // const [jobId, setJobId] = useState<string>(urlJobId || propJobId);
  console.log(jobId, "jobId");
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState<number>(5 * 60); // 5 minutes in seconds
  const [timerActive, setTimerActive] = useState<boolean>(fromNextButton);
  const [timerPercent, setTimerPercent] = useState<number>(100);
  const [contributionChartData, setContributionChartData] = useState<number[]>(
    []
  );
  const [contributionChartLabel, setContributionChartLabel] = useState<
    string[]
  >([]);
  const [saturationCharts, setSaturationCharts] = useState<React.ReactNode[]>(
    []
  );
  const [yearlyOptimizedSpendChartData, setYearlyOptimizedSpendChartData] =
    useState<number[]>([]);
  const [yearlyOptimizedSpendChartLabel, setYearlyOptimizedSpendChartLabel] =
    useState<string[]>([]);
  const [mediaYOYSpentChart, setMediaYOYSpentChart] = useState<
    MultiBarLineChartProps | undefined
  >(undefined);
  const [roiFinalData, setRoiFinalData] = useState<any[]>([]);
  const [spendShare, setsSendShare] = useState<any>({});
  const [actualPredictedLabels, setActualPredictedLabels] = useState<string[]>(
    []
  );
  const [actualSales, setActualSales] = useState<number[]>([]);
  const [predictedSales, setPredictedSales] = useState<number[]>([]);

  // Initialize with empty arrays instead of dummy data
  const [y1, setY1] = useState<number[]>([]);
  const [y2, setY2] = useState<number[]>([]);
  const [correlationLabels, setCorrelationLabels] = useState<string[]>([]);
  const [rawContributionData, setRawContributionData] = useState<
    Record<string, number>
  >({});
  const [rawEffectData, setRawEffectData] = useState<Map<string, number>>(
    new Map()
  );

  const [dateVariables, setDateVariables] = useState<string[]>([]);
  const [mediaVariables, setMediaVariables] = useState<string[]>([]);
  const [targetVariables, setTargetVariables] = useState<string[]>([]);
  const [marketLocations, setMarketLocations] = useState<string[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<string>("");
  const [waterFallChartLabel, setWaterFallChartLabel] = useState<
    string[] | undefined
  >(undefined);
  const [waterFallChartData, setWaterFallChartData] = useState<
    number[] | undefined
  >(undefined);
  const [salesDecompositionLabel, setSalesDecompositionLabel] = useState<
    string[]
  >([]);
  const [salesDecompositionData, setSalesDecompositionData] = useState<any[]>(
    []
  );
  const [historicSpendContributionLabel, setHistoricSpendContributionLabel] =
    useState<string[]>([]);
  const [historicSpendContributionData, setHistoricSpendContributionData] =
    useState<number[]>([]);
  const [effectShareLabel, setEffectShareLabel] = useState<string[]>([]);
  const [effectShareData, setEffectShareData] = useState<number[]>([]);
  const [elasticityColumn, setElasticityColumn] = useState<TableColumn[]>([]);
  const [elasticityRecord, setElasticityRecord] = useState<TableRecord[]>([]);
  const [seasonDataSet, setSeasonDataSet] = useState<LineData[]>([]);
  const [trendDataSet, setTrendDataSet] = useState<LineData[]>([]);
  const [seasonLabel, setSeasonLabel] = useState<string[]>([]);
  const [modelAccuracy, setModelAccuracy] = useState<any[]>([]);
  const [heatMapData, setHeatMapData] = useState<any>(undefined);
  const [similarityHeatMapData, setSimilarityHeatMapData] =
    useState<any>(undefined);
  const [adstockData, setAdstockData] = useState<any>(undefined);
  const [multiColinearityRecords, setMultiColinearityRecords] = useState<
    TableRecord[]
  >([]);
  const [isLoading, setLoading] = useState<boolean>(true);
  // The initial page load is actually 4 independent network calls spread
  // across two useEffects (output JSON, job config, contribution %, and a
  // second output-JSON fetch nested inside the job-config one). isLoading
  // used to be flipped false by whichever of those finished first — usually
  // the plain output-JSON call — so the spinner disappeared while the other
  // three were still in flight, showing a blank/partial screen. This tracks
  // all four and only clears isLoading once every one of them has settled
  // (success or failure). Doesn't affect the separate setLoading(true/false)
  // calls in runOptimizeMediaSpend, which gate a single, already-sequential
  // fetch and have no race to fix.
  const initialLoadRef = useRef({
    output: false,
    jobConfig: false,
    contribution: false,
    output2: false,
  });
  const markInitialFetchDone = (key: keyof typeof initialLoadRef.current) => {
    initialLoadRef.current[key] = true;
    if (Object.values(initialLoadRef.current).every(Boolean)) {
      setLoading(false);
    }
  };
  const [showChartLabels, setShowChartLabels] = useState<boolean>(false);
  const [jobConfig, setJobConfig] = useState<any>(null);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [selectedSaturation, setSelectedSaturation] = useState<
    string | undefined
  >(undefined);
  const [mmmOutputJson, setMmmOutputJson] = useState<any>(null);
  // Fetch and set MMM output JSON
  useEffect(() => {
    if (!jobId) return;
    setLoading(true);
    // Reset just the flag this effect owns — if jobId changes, both this
    // effect and the job-config one below re-run together (jobId is in
    // both dependency arrays), so their resets combine into a fresh
    // all-false state. If only `fromNextButton` changes, this effect won't
    // re-run, so "output" correctly stays at its already-settled value.
    initialLoadRef.current.output = false;
    axiosInstance
      .get(`/api/mmm/output/${jobId}`)
      .then((outputResponse) => {
        let outputJson = outputResponse.data.result;
        if (typeof outputJson === "string") {
          try {
            outputJson = JSON.parse(outputJson);
          } catch (error) {
            console.error("Failed to parse JSON string:", error);
          }
        }

        // Log saturation data for debugging
        const saturationKeys = Object.keys(outputJson || {}).filter((key) =>
          key.startsWith("Saturation_")
        );
        const saturationKeysVariables = Object.keys(outputJson || {})
          .filter((key) => key.startsWith("Saturation_"))
          .map((key) => key.replace(/^Saturation_/, ""));
        setSpendVariables(saturationKeysVariables);

        if (saturationKeys.length > 0) {
          console.log("Found saturation data keys:", saturationKeys);
          // Log the first saturation data item as an example
          const firstKey = saturationKeys[0];
          console.log(
            `Sample data for ${firstKey}:`,
            Array.isArray(outputJson[firstKey]) &&
              outputJson[firstKey].length > 0
              ? outputJson[firstKey][0]
              : outputJson[firstKey]
          );
        } else {
          console.log("No saturation data found in the response");
        }

        // Log adstock data for debugging
        if (outputJson?.adstock) {
          console.log(
            "Found adstock data:",
            Array.isArray(outputJson.adstock) && outputJson.adstock.length > 0
              ? outputJson.adstock.slice(0, 2) // Show first two items
              : outputJson.adstock
          );
        } else {
          console.log("No adstock data found in the response");
        }

        setMmmOutputJson(outputJson);
      })
      .catch((error) => {
        console.error("Failed to fetch MMM output:", error);
        setMmmOutputJson(null);
      })
      .finally(() => markInitialFetchDone("output"));
  }, [jobId]);

  // Build saturation chart options
  const saturationChartOptions = useMemo(() => {
    if (!mmmOutputJson) return [];

    const colors = [
      "rgba(75, 192, 192, 1)",
      "rgba(255, 99, 132, 1)",
      "rgba(54, 162, 235, 1)",
      "rgba(255, 206, 86, 1)",
      "rgba(153, 102, 255, 1)",
      "rgba(255, 159, 64, 1)",
    ];

    // Helper function to extract data from different possible formats
    const extractSaturationData = (key: string, data: any[]) => {
      // Extract the variable name without the "Saturation_" prefix
      const variableName = key.replace("Saturation_", "");

      // Try different approaches to extract the data

      // Approach 1: Check if data has Spends and variable name properties
      if (
        data[0]?.Spends !== undefined &&
        data[0]?.[variableName] !== undefined
      ) {
        return {
          labels: data.map((item) =>
            item.Spends.startsWith("Line")
              ? item.Spends.slice("Line".length)
              : item.Spends
          ),
          values: data.map((item) => item[variableName]),
        };
      }

      // Approach 2: Check if data has mean_input_total and key properties
      if (
        data[0]?.mean_input_total !== undefined &&
        data[0]?.[key] !== undefined
      ) {
        return {
          labels: data.map((item) => item.mean_input_total),
          values: data.map((item) => item[key]),
        };
      }

      // Approach 3: Check if data has x and y properties
      if (data[0]?.x !== undefined && data[0]?.y !== undefined) {
        return {
          labels: data.map((item) => item.x),
          values: data.map((item) => item.y),
        };
      }

      // Approach 4: Check if data has input and output properties
      if (data[0]?.input !== undefined && data[0]?.output !== undefined) {
        return {
          labels: data.map((item) => item.input),
          values: data.map((item) => item.output),
        };
      }

      // Approach 5: Check if data is an array of arrays [x, y]
      if (Array.isArray(data[0]) && data[0].length === 2) {
        return {
          labels: data.map((item) => item[0]),
          values: data.map((item) => item[1]),
        };
      }

      // Fallback: Try to find any numeric properties
      const firstItem = data[0];
      const numericKeys = Object.keys(firstItem).filter(
        (k) => typeof firstItem[k] === "number" && k !== "id" && k !== "index"
      );

      if (numericKeys.length >= 2) {
        // Use the first two numeric properties as x and y
        return {
          labels: data.map((item) => item[numericKeys[0]]),
          values: data.map((item) => item[numericKeys[1]]),
        };
      }

      // Last resort: Generate sequential x values and use any available numeric value for y
      if (numericKeys.length === 1) {
        return {
          labels: Array.from({ length: data.length }, (_, i) => i + 1),
          values: data.map((item) => item[numericKeys[0]]),
        };
      }

      // If all else fails, return empty arrays
      return { labels: [], values: [] };
    };

    return Object.keys(mmmOutputJson)
      .filter((key) => key.startsWith("Saturation_"))
      .map((key, idx) => {
        const data = mmmOutputJson[key];
        if (!data || !Array.isArray(data) || data.length === 0) return null;

        // Format the dataset name properly
        const datasetName = key
          .replace("Saturation_", "")
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
          .replace(/_/g, " ");

        const color = colors[idx % colors.length];

        // Extract data using our helper function
        const { labels, values } = extractSaturationData(key, data);

        // Skip if we couldn't extract any meaningful data
        if (labels.length === 0 || values.length === 0) return null;

        // Create a point for the last data point (used for annotations)
        const point = {
          x: labels[labels.length - 1],
          y: values[values.length - 1],
        };

        return {
          key,
          label: datasetName.startsWith("Line")
            ? datasetName.slice("Line".length)
            : datasetName,
          chartProps: {
            labels,
            dataSets: [
              {
                name: datasetName,
                data: values,
                borderColor: color,
                backgroundColor: color.replace("1)", "0.3)"),
              },
            ],
            xAxisTitle: "Spends",
            yAxisTitle: "Response",
            point,
          },
        };
      })
      .filter(
        (v): v is { key: string; label: string; chartProps: any } => v !== null
      );
  }, [mmmOutputJson]);

  // Always get the selected option safely
  const selectedOption = useMemo(
    () => saturationChartOptions.find((opt) => opt.key === selectedSaturation),
    [saturationChartOptions, selectedSaturation]
  );
  // Set default selectedSaturation when options load
  useEffect(() => {
    if (saturationChartOptions.length > 0 && !selectedSaturation) {
      setSelectedSaturation(saturationChartOptions[0].key);
    }
  }, [saturationChartOptions, selectedSaturation]);

  // Generate fallback data if no saturation data is available
  const generateFallbackSaturationData = useCallback(() => {
    if (saturationChartOptions.length === 0 && mediaVariables.length > 0) {
      // Create synthetic data for demonstration
      const fallbackData = mediaVariables.map((variable, idx) => {
        const color = [
          "rgba(75, 192, 192, 1)",
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ][idx % 6];

        // Generate synthetic data points for a saturation curve
        const points = 10;
        const labels = Array.from({ length: points }, (_, i) =>
          Math.round((i + 1) * 10000)
        );

        // Create a curve that shows diminishing returns
        const lineData = labels.map((_, i) => {
          const x = i / (points - 1);
          // Formula for diminishing returns curve: y = 1 - e^(-3x)
          return (1 - Math.exp(-3 * x)) * 100;
        });

        return {
          key: `Saturation_${variable}`,
          label: variable
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          chartProps: {
            labels,
            dataSets: [
              {
                name: variable,
                data: lineData,
                borderColor: color,
                backgroundColor: color.replace("1)", "0.3)"),
              },
            ],
            xAxisTitle: "Spends",
            yAxisTitle: "Response",
            point: {
              x: labels[labels.length - 1],
              y: lineData[lineData.length - 1],
            },
          },
        };
      });

      return fallbackData;
    }
    return [];
  }, [saturationChartOptions.length, mediaVariables]);

  // Helper function to normalize data
  const normalizeData = (data: any[]): number[] => {
    if (!data || data.length === 0) return [];
    const min = Math.min(...data);
    const max = Math.max(...data);
    return data.map((value: number) => (value - min) / (max - min));
  };

  // Generate fallback data for adstock chart if real data is not available
  const generateFallbackAdstockData = useCallback(() => {
    if (!adstockData && mediaVariables.length > 0) {
      // Format the media variables for display
      const labels = mediaVariables.map((variable) =>
        variable
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      );

      // Generate random but realistic adstock values (between 0.1 and 0.9)
      const thetaValues = mediaVariables.map(
        () => Math.round((0.1 + Math.random() * 0.8) * 100) / 100
      );

      // Generate random but realistic strength values (between 0.2 and 0.8)
      const strengthValues = mediaVariables.map(
        () => Math.round((0.2 + Math.random() * 0.6) * 100) / 100
      );

      // Generate random but realistic length values (between 1 and 4)
      const lengthValues = mediaVariables.map(() =>
        Math.round(1 + Math.random() * 3)
      );

      const fallbackAdstockData = {
        labels,
        barDatasets: [
          {
            name: "Theta",
            values: thetaValues,
            color: "#73C2FB", // Blue
          },
          {
            name: "Alpha",
            values: strengthValues,
            color: "#FF6F61", // Red
          },
          {
            name: "Gamma",
            values: lengthValues,
            color: "#32CD32", // Green
          },
        ],
        lineDatasets: [],
      };

      return fallbackAdstockData;
    }
    return null;
  }, [adstockData, mediaVariables]);

  // Save jobId to URL if coming from next button
  useEffect(() => {
    if (fromNextButton && propJobId) {
      // Update URL with jobId parameter
      setSearchParams({ jobId: propJobId, fromNext: "true" });
    }
  }, [fromNextButton, propJobId, setSearchParams]);

  // Timer functionality
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          const newTime = prev - 1;
          // Calculate percentage for progress bar
          setTimerPercent(Math.floor((newTime / (5 * 60)) * 100));
          return newTime;
        });
      }, 1000);
    } else if (timeRemaining === 0) {
      // Refresh the page when timer reaches zero
      window.location.reload();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerActive, timeRemaining]);

  // Format time remaining as MM:SS
  const formatTimeRemaining = useCallback(() => {
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }, [timeRemaining]);

  // Fetch correlation data
  // const fetchCorrelationData = (y1: string, y2: string, x: string): void => {
  //   axiosInstance
  //     .post(MMM_INPUT_DATA, {
  //       jobConfigId: jobId,
  //       tenantId: getTenantId(),
  //       y1,
  //       y2,
  //       x,
  //     })
  //     .then((response) => {
  //       setCorrelationLabels(response.data["date"]);
  //       setY1(normalizeData(response.data["y1"]));
  //       setY2(normalizeData(response.data["y2"]));
  //     })
  //     .catch((error) => {
  //       console.error("Error fetching correlation data:", error);
  //       message.error("Failed to fetch correlation data");
  //     });
  // };

  // // Handle correlation media variable change
  // const onCorrelationMediaVariableChange = (value: string): void => {
  //   fetchCorrelationData(
  //     value,
  //     targetVariables[0],
  //     dateVariables[0]
  //   );
  // };

  // // Handle market location change
  // const onMarketLocationChange = (value: string): void => {
  //   setSelectedMarket(value);
  // };

  // Run optimize media spend
  const runOptimizeMediaSpend = (data: any): void => {
    setLoading(true);

    const payload = {
      jobConfigId: jobId,
      tenantId: getTenantId(),
      ...data,
    };

    axiosInstance
      .post(OPTIMIZE_SPEND_API, payload)
      .then((response) => {
        // Process response data for scenarios
        const responseData = response.data;

        Object.keys(scenarioData).forEach((key) => {
          if (responseData[key]) {
            scenarioData[key] = {
              ...scenarioData[key],
              yearlySpendChartData: responseData[key].yearlySpendChartData,
              contributionChartData: responseData[key].contributionChartData,
              labels: responseData[key].labels,
            };
          }
        });

        setLoading(false);
      })
      .catch((error) => {
        console.error("Error optimizing media spend:", error);
        message.error("Failed to optimize media spend");
        setLoading(false);
      });
  };

  // Generate PDF function
  const generatePDF = async (): Promise<void> => {
    if (!contentRef.current) return;

    try {
      setExportLoading(true);
      message.loading({ content: "Generating PDF...", key: "pdfExport" });

      const content = contentRef.current;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Capture full content as image
      const canvas = await html2canvas(content, {
        scale: 2, // Higher scale = better quality
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 10;

      // First page
      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      // Additional pages if needed
      while (heightLeft > 0) {
        position = 0;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save("marketing_mix_model_report.pdf");

      message.success({
        content: "PDF generated successfully!",
        key: "pdfExport",
        duration: 2,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error({
        content: "Failed to generate PDF. Please try again.",
        key: "pdfExport",
        duration: 2,
      });
    } finally {
      setExportLoading(false);
    }
  };

  const generateSaturationCharts = (response: any) => {
    // Check if response is defined
    if (!response) {
      console.warn("Saturation data is undefined");
      return [];
    }

    const colors = [
      "rgba(75, 192, 192, 1)",
      "rgba(255, 99, 132, 1)",
      "rgba(54, 162, 235, 1)",
      "rgba(255, 206, 86, 1)",
      "rgba(153, 102, 255, 1)",
      "rgba(255, 159, 64, 1)",
    ];

    const charts: any[] = [];
    const allKeys = Object.keys(response);

    allKeys.forEach((key, index) => {
      if (key.startsWith("Saturation_")) {
        const data = response[key];

        // Check if data is defined and is an array
        if (!data || !Array.isArray(data) || data.length === 0) {
          console.warn(`Saturation data for ${key} is undefined or empty`);
          return;
        }

        const datasetName = key
          .replaceAll("Saturation_", "")
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
          .replaceAll(/_/g, " ");
        const color = colors[index % colors.length];

        // Add null checks for data mapping
        const labels = data.map((item: any) => item?.Spends || 0);
        const lineData = data.map((item: any) => {
          const fieldName = key.replaceAll("Saturation_", "");
          return item?.[fieldName] || 0;
        });

        const chartProps: LineChartProps = {
          labels,
          dataSets: [
            {
              name: datasetName,
              data: lineData,
              borderColor: color,
              backgroundColor: color.replaceAll("1)", "0.3)"),
            },
          ],
          xAxisTitle: "Spends",
          yAxisTitle: datasetName,
        };

        charts.push(
          <div key={`${key}-${index}`} className="w-fill">
            <div className="w-full">
              <Maximize title={datasetName}>
                <LineChart key={key} {...chartProps} />
              </Maximize>
            </div>
          </div>
        );
      }
    });

    return charts;
  };

  // Fetch job config and data
  useEffect(() => {
    if (!jobId) return;

    // Reset the three flags this effect (and its nested fetches) own — see
    // the matching reset in the output-JSON effect above for why.
    initialLoadRef.current.jobConfig = false;
    initialLoadRef.current.contribution = false;
    initialLoadRef.current.output2 = false;

    // Fetch job config
    axiosInstance
      .get(`/api/jobConfig/${jobId}`)
      .then((response) => {
        // Optional chaining + fallbacks — a partial/missing columnMapping
        // (e.g. a job created via a path that didn't populate every key)
        // must not throw here, or this whole view renders blank with no
        // indication why (the error only surfaces in the console).
        const columnMapping =
          response.data?.dataMappingConfig?.mappings?.mmm?.columnMapping || {};
        const mediaVariables = columnMapping.media_variables || [];
        const targetVariables = columnMapping.target_variables || [];
        const dateVariables = columnMapping.date_variables || [];
        const locations = columnMapping.market_locations || [];

        setMediaVariables(mediaVariables);
        setTargetVariables(targetVariables);
        setDateVariables(dateVariables);
        setMarketLocations(locations);
        setJobConfig(response.data);

        if (locations && locations.length > 0) {
          setSelectedMarket(locations[0]);
        }

        // Fetch correlation data for initial view
        if (
          mediaVariables.length > 0 &&
          targetVariables.length > 0 &&
          dateVariables.length > 0
        ) {
          fetchCorrelationData(
            mediaVariables[0],
            targetVariables[0],
            dateVariables[0]
          );
        }

        // Fetch contribution data
        axiosInstance
          .post(CONTRIBUTION_PERC_API, {
            spendColumns: mediaVariables,
            jobConfigId: jobId,
            tenantId: getTenantId(),
          })
          .then((contribResponse) => {
            const contributionData: Record<string, number> =
              contribResponse.data.totalContribution || {};
            setRawContributionData(contributionData);
            // const labels: string[] = Object.keys(contributionData);
            // const data: number[] = Object.values(contributionData);
            // setHistoricSpendContributionLabel(labels);
            // setHistoricSpendContributionData(data);
          })
          .catch((error) => {
            console.error("Error fetching contribution data:", error);
          })
          .finally(() => markInitialFetchDone("contribution"));

        // Fetch MMM output data
        axiosInstance
          .get(`/api/mmm/output/${jobId}`)
          .then((outputResponse) => {
            let outputJson = outputResponse.data.result;
            if (typeof outputJson === "string") {
              try {
                outputJson = JSON.parse(outputJson);
              } catch (error) {
                console.error("Failed to parse JSON string:", error);
              }
            }
            console.log("MMM Output:", outputJson);

            const result = Object.entries(outputJson)
              .filter(([key]) => key.startsWith("Saturation_")) // 1. Filter keys
              .map(([, value]: [any, any]) => value[0]); // 2. Get first object from each array

            setRoiFinalData(result); // 3. Save to state
            setsSendShare(() => ({
              Contributions_chart: outputJson.Contributions_chart,
              spendShare: outputJson.spendShare[0],
            }));
            // Process all the data from outputJson
            // if (outputJson.Saturation_Competitor_Spend) {
            //   setRoiFinalData(outputJson.Saturation_Competitor_Spend);
            //   setRoiLabels(
            //     outputJson.ROI_Final.map((item: any) => item.MediaChannel)
            //   );
            // }

            setModelAccuracy(outputJson["ModelAccuracy"] || []);
            const seasonalityData = outputJson["Seasonality"];

            if (seasonalityData) {
              const labels = seasonalityData.map(
                (item: any) => item[dateVariables[0]]
              );
              const seasonData = seasonalityData.map((item: any) =>
                parseFloat(item.Season.toFixed(2))
              );
              const trendData = seasonalityData.map((item: any) =>
                parseFloat(item.Trend.toFixed(2))
              );

              setSeasonLabel(labels);
              setSeasonDataSet([
                {
                  name: "Season",
                  data: seasonData,
                  borderColor: "rgba(75, 192, 192, 1)",
                  backgroundColor: "rgba(75, 192, 192, 0.3)",
                },
              ]);
              setTrendDataSet([
                {
                  name: "Trend",
                  data: trendData,
                  borderColor: "rgba(255, 99, 132, 1)",
                  backgroundColor: "rgba(255, 99, 132, 0.3)",
                },
              ]);
            }

            // Process YoY Sales Contribution chart
            const yoySpentData = outputJson["YoY_Sales_Contribution_chart"];
            if (yoySpentData) {
              const colorPalette = [
                "#FFC300",
                "#FF5733",
                "#C70039",
                "#900C3F",
                "#581845",
                "#1F618D",
                "#117A65",
                "#F39C12",
              ];

              const barDatasets = Object.keys(yoySpentData[0])
                .filter((key) =>
                  [...mediaVariables, "Base", "Seasonality"].includes(key)
                )
                .map((key, index) => ({
                  name: key.replace(/_/g, " "),
                  values: yoySpentData.map((item: any) =>
                    Math.round(item[key])
                  ),
                  color: colorPalette[index % colorPalette.length],
                }));

              const labels = yoySpentData.map((item: any) =>
                item.Year.toString()
              );
              const chartProps: MultiBarLineChartProps = {
                labels,
                barDatasets,
                lineDatasets: [],
              };
              setMediaYOYSpentChart(chartProps);
            }

            // Process correlation matrix
            const correlationMatrix = outputJson["CorrelationMatrix"];
            if (correlationMatrix) {
              const targetVariableKey = targetVariables[0];
              const xLabels = Object.keys(correlationMatrix[0]).sort((a, b) => {
                if (a === targetVariableKey) return -1;
                if (b === targetVariableKey) return 1;
                return a.localeCompare(b);
              });
              const yLabels = xLabels;

              const sortedMatrix = xLabels.map((label) => {
                return correlationMatrix.find(
                  (row: { [x: string]: number }) => row[label] === 1
                );
              });

              const zData = sortedMatrix.map((row: any) =>
                xLabels.map((x) => row[x])
              );

              setHeatMapData({
                xLabels,
                yLabels,
                zData,
              });
            }

            // Process similarity matrix
            const similarityMatrix = outputJson["Similarity"];
            if (similarityMatrix) {
              const targetVariableKey = targetVariables[0];
              const xLabels = Object.keys(similarityMatrix[0]).sort((a, b) => {
                if (a === targetVariableKey) return -1;
                if (b === targetVariableKey) return 1;
                return a.localeCompare(b);
              });
              const yLabels = xLabels;

              const zData = similarityMatrix.map((row: any) =>
                xLabels.map((x) => row[x])
              );

              setSimilarityHeatMapData({
                xLabels,
                yLabels,
                zData,
              });
            }
            // Process adstock data
            try {
              const adstock = outputJson["adstock"];
              if (adstock && Array.isArray(adstock) && adstock.length > 0) {
                console.log("Found adstock data:", adstock.slice(0, 2));

                // Check the structure of the adstock data
                const firstItem = adstock[0];

                // Determine the format of the adstock data
                let groupedData: Record<string, any> = {};

                // Format 1: {Var_Name: string, Adstock_Value: number}
                if (
                  firstItem.Var_Name &&
                  firstItem.Adstock_Value !== undefined
                ) {
                  groupedData = adstock.reduce((acc: any, item: any) => {
                    // Split the variable name into components
                    const prefix = item.Var_Name;
                    // Extract prefix (everything before the last component)
                    const metric = item.Type;

                    // Add the Adstock_Value to the correct metric
                    acc[prefix][metric] = item.Adstock_Value;

                    return acc;
                  }, {});
                }
                // If we have grouped data, create the chart
                if (Object.keys(groupedData).length > 0) {
                  const labels = Object.keys(groupedData);

                  // Determine which metrics are available
                  const availableMetrics = new Set<string>();
                  for (const label of labels) {
                    for (const metric of Object.keys(groupedData[label])) {
                      //if (groupedData[label][metric] !== 0)
                      {
                        availableMetrics.add(metric);
                      }
                    }
                  }

                  // Create datasets based on available metrics
                  const barDatasets: any[] = [];

                  // Add theta/adstock if available
                  if (availableMetrics.has("theta")) {
                    barDatasets.push({
                      name: "Adstock",
                      values: labels.map((label) => groupedData[label]?.theta),
                      color: "#73C2FB", // Blue
                    });
                  }

                  // Add alpha/strength if available
                  if (availableMetrics.has("alpha")) {
                    barDatasets.push({
                      name: "alpha",
                      values: labels.map((label) => groupedData[label]?.alpha),
                      color: "#FF6F61", // Red
                    });
                  }

                  // Add gamma/length if available
                  if (availableMetrics.has("gamma")) {
                    barDatasets.push({
                      name: "gamma",
                      values: labels.map((label) => groupedData[label]?.gamma),
                      color: "#32CD32", // Green
                    });
                  }

                  // If no specific metrics were found, use a generic approach
                  if (barDatasets.length === 0) {
                    // Just use the first numeric property we find
                    const firstLabel = labels[0];
                    const firstMetric = Object.keys(
                      groupedData[firstLabel]
                    ).find(
                      (key) => typeof groupedData[firstLabel][key] === "number"
                    );

                    if (firstMetric) {
                      barDatasets.push({
                        name:
                          firstMetric.charAt(0).toUpperCase() +
                          firstMetric.slice(1),
                        values: labels.map(
                          (label) => groupedData[label]?.[firstMetric]
                        ),
                        color: "#73C2FB", // Blue
                      });
                    }
                  }

                  const chartProps: any = {
                    labels: labels.map((label) =>
                      label
                        .split("_")
                        .map(
                          (word) => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ")
                    ),
                    barDatasets,
                    lineDatasets: [], // No line datasets needed
                  };

                  console.log("Generated adstock chart props:", chartProps);
                  setAdstockData(chartProps);
                } else {
                  console.log(
                    "Could not process adstock data format:",
                    firstItem
                  );
                  // Use media variables to create fallback data
                  if (mediaVariables.length > 0) {
                    const fallbackData = generateFallbackAdstockData();
                    if (fallbackData) {
                      //setAdstockData(fallbackData);
                    }
                  }
                }
              } else {
                console.log("No adstock data found or invalid format");
                // Use media variables to create fallback data
                if (mediaVariables.length > 0) {
                  const fallbackData = generateFallbackAdstockData();
                  if (fallbackData) {
                    //setAdstockData(fallbackData);
                  }
                }
              }
            } catch (error) {
              console.error("Error processing adstock data:", error);
              // Use media variables to create fallback data
              if (mediaVariables.length > 0) {
                const fallbackData = generateFallbackAdstockData();
                if (fallbackData) {
                  //setAdstockData(fallbackData);
                }
              }
            }

            // Process VIF factors
            if (outputJson["VifFactors"]) {
              const records: TableRecord[] = [];
              outputJson["VifFactors"]?.forEach(
                (item: { Variable: string; VIF: number }) => {
                  records.push({
                    variable: item.Variable.split("_")
                      ?.map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" "),
                    value: item.VIF,
                  });
                }
              );
              setMultiColinearityRecords(records);
            }

            // Process other data
            setSalesDecompositionData(outputJson["Sales_Decomposition"] || []);
            setSalesDecompositionLabel(
              outputJson["Sales_Decomposition"]?.map(
                (item: any, index: number) => index.toString()
              ) || []
            );

            const charts = generateSaturationCharts(outputJson);
            setSaturationCharts(charts);

            setContributionChartData(
              outputJson["Final_Optimizer"]?.map((item: any) =>
                Number.parseFloat(item.Contribution)
              ) || []
            );
            setContributionChartLabel(
              outputJson["Final_Optimizer"]?.map((item: any) => item.Media) ||
                []
            );

            setYearlyOptimizedSpendChartData(
              outputJson["Final_Optimizer"]?.map((item: any) =>
                Number.parseFloat(item.Yearly_Optimize_Spend)
              ) || []
            );
            setYearlyOptimizedSpendChartLabel(
              outputJson["Final_Optimizer"]?.map((item: any) => item.Media) ||
                []
            );

            if (outputJson["Contributions_chart"]) {
              const effectData: ContributionItem[] =
                outputJson?.Contributions_chart?.filter(
                  (item: ContributionItem) =>
                    item.Variables !== "Base" &&
                    item.Variables !== "Seasonality"
                ) || [];
              const totalEffect = effectData.reduce(
                (sum: number, item: ContributionItem) =>
                  sum + Number(item.Contributions),
                0
              );

              // Store contributions as percentages
              const effectMap = new Map<string, number>();
              for (const item of effectData) {
                const percentage =
                  totalEffect > 0
                    ? (Number(item.Contributions) / totalEffect) * 100
                    : 0;
                effectMap.set(
                  item.Variables,
                  parseFloat(percentage.toFixed(2))
                );
              }

              setRawEffectData(effectMap);
              // const labels = effectData?.map((item: any) => item.Variables);
              // const values = effectData?.map((item: any) =>
              //   Number(item.Contributions)
              // );

              // setEffectShareLabel(labels);
              // setEffectShareData(values);
            }

            setActualPredictedLabels(
              outputJson["Actual_vs_predicted"]
                ?.map((item: any) => {
                  const dateKey = Object.keys(item)?.find(
                    (key) => key.trim() === "Date"
                  );
                  if (dateKey) {
                    return item[dateKey];
                  }
                  return null;
                })
                .filter((date: any) => date !== null) || []
            );
            setActualSales(
              outputJson["Actual_vs_predicted"]?.map(
                (item: any) => item[targetVariables[0]]
              ) || []
            );
            setPredictedSales(
              outputJson["Actual_vs_predicted"]?.map(
                (item: any) => item.prediction
              ) || []
            );

            const sortedContributionsChart =
              outputJson.Contributions_chart?.sort((a: any, b: any) => {
                const isAImportant =
                  a.Variables === "Seasonality" || a.Variables === "Base";
                const isBImportant =
                  b.Variables === "Seasonality" || b.Variables === "Base";

                if (isAImportant && isBImportant) {
                  return b.Contributions - a.Contributions;
                }

                if (isAImportant) return -1;
                if (isBImportant) return 1;

                return 0;
              });

            const formattedLabels: string[] = [];
            const formattedData: number[] = [];

            sortedContributionsChart?.forEach((item: any) => {
              const value = Number.parseInt(item.Contributions);
              const formattedValue =
                value >= 100000
                  ? `$${(value / 1000).toFixed(0)}K`
                  : `$${value}`;

              formattedLabels.push(`${item.Variables} (${formattedValue})`);
              formattedData.push(value); // keep numeric if your chart expects numbers
            });

            setWaterFallChartLabel(formattedLabels);
            setWaterFallChartData(formattedData);

            setElasticityColumn(
              Object.keys(outputJson?.Elasticity_Report?.[0] || {})
                .sort((a, b) => {
                  if (a === "MediaChannel") return -1;
                  if (b === "MediaChannel") return 1;
                  return 0;
                })
                .map((key) => ({
                  key: key.trim(),
                  label: key.trim().replaceAll("_", " "),
                }))
            );
            setElasticityRecord(
              outputJson.Elasticity_Report?.map((item: any) => {
                const record: TableRecord = {};
                Object.keys(item).forEach((key) => {
                  const formattedKey = key
                    .trim()
                    .split("_")
                    .map(
                      (word: string) =>
                        word.charAt(0).toUpperCase() + word.slice(1)
                    )
                    .join(" ");

                  let formattedValue = item[key];
                  if (typeof formattedValue === "string") {
                    formattedValue = formattedValue
                      .trim()
                      .split("_")
                      .map(
                        (word: string) =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ");
                  }

                  record[formattedKey] = formattedValue;
                });
                return record;
              }) || []
            );

            // Initialize scenarioData with proper structure
            scenarioData = {
              "Scenario 1": {
                variables: { "Total Budget": "", Weeks: "" },
                yearlySpendChartData: [],
                contributionChartData: [],
                labels: [],
              },
              "Scenario 2": {
                variables: { "Total Budget": "", Weeks: "" },
                yearlySpendChartData: [],
                contributionChartData: [],
                labels: [],
              },
              "Scenario 3": {
                variables: { "Total Budget": "", Weeks: "" },
                yearlySpendChartData: [],
                contributionChartData: [],
                labels: [],
              },
              "Scenario 4": {
                variables: { "Total Budget": "", Weeks: "" },
                yearlySpendChartData: [],
                contributionChartData: [],
                labels: [],
              },
              "Scenario 5": {
                variables: { "Total Budget": "", Weeks: "" },
                yearlySpendChartData: [],
                contributionChartData: [],
                labels: [],
              },
            };

            if (outputJson) {
              const colors = [
                "rgba(75, 192, 192, 1)",
                "rgba(255, 99, 132, 1)",
                "rgba(54, 162, 235, 1)",
                "rgba(255, 206, 86, 1)",
                "rgba(153, 102, 255, 1)",
                "rgba(255, 159, 64, 1)",
              ];
              const options = Object.keys(outputJson)
                .filter((key) => key.startsWith("Saturation_"))
                .map((key, idx) => {
                  const data = outputJson[key];
                  if (!data || !Array.isArray(data) || data.length === 0)
                    return null;
                  const datasetName = key
                    .replace("Saturation_", "")
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                    .replace(/_/g, " ");
                  const color = colors[idx % colors.length];
                  const labels = data.map((item: any) => item?.Spends ?? 0);
                  const lineData = data.map((item: any) => {
                    const fieldName = key.replace("Saturation_", "");
                    return item?.[fieldName] ?? 0;
                  });
                  const point = {
                    x: labels[labels.length - 1],
                    y: lineData[lineData.length - 1],
                  };
                  return {
                    key,
                    label: datasetName,
                    chartProps: {
                      labels,
                      dataSets: [
                        {
                          name: datasetName,
                          data: lineData,
                          borderColor: color,
                          backgroundColor: color.replace("1)", "0.3)"),
                        },
                      ],
                      xAxisTitle: "Spends",
                      yAxisTitle: datasetName,
                      point,
                    },
                  };
                })
                .filter(Boolean);
            }
          })
          .catch((error) => {
            console.error("Error fetching MMM output data:", error);
            message.error("Failed to fetch analysis data");
          })
          .finally(() => markInitialFetchDone("output2"));

        // The synchronous part of handling the job-config response (the
        // setX calls + firing the two fetches above) is done — this fetch
        // itself is complete. The two fetches above track their own
        // completion independently via their own .finally() calls.
        markInitialFetchDone("jobConfig");
      })
      .catch((error) => {
        console.error("Error fetching job config:", error);
        message.error("Failed to fetch job configuration");
        // jobConfig failed, so the two nested fetches above never fired —
        // mark them done too, or isLoading would be stuck true forever.
        markInitialFetchDone("jobConfig");
        markInitialFetchDone("contribution");
        markInitialFetchDone("output2");
      });
  }, [jobId, fromNextButton]);

  const onCorrelationMediaVariableChange = (data: any) => {
    fetchCorrelationData(
      data.target.value,
      targetVariables[0],
      dateVariables[0]
    );
  };

  const onMarketLocationChange = (data: any) => {
    setSelectedMarket(data.target.value);
  };

  const fetchCorrelationData = (y1: string, y2: string, x: string) => {
    axiosInstance
      .post(MMM_INPUT_DATA, {
        jobConfigId: jobId,
        tenantId: getTenantId(),
        y1,
        y2,
        x,
      })
      .then((response) => {
        setCorrelationLabels(response.data["date"]);
        setY1(normalizeData(response.data["y1"]));
        setY2(normalizeData(response.data["y2"]));
      });
  };

  // Prepare chart data
  const correlationChart: LineChartProps = {
    labels: correlationLabels,
    dataSets: [
      {
        name: "Media Variable",
        data: y1,
        borderColor: "#ff0000",
        backgroundColor: "rgba(255, 0, 0, 0.3)",
      },
      {
        name: "Target Variable",
        data: y2,
        borderColor: "#0000ff",
        backgroundColor: "rgba(0, 0, 255, 0.3)",
      },
    ],
    xAxisTitle: "Days",
    yAxisTitle: "Sales",
  };

  const twoLineChartData: LineChartProps = {
    labels: actualPredictedLabels,
    dataSets: [
      {
        name: "Actual Sales",
        data: actualSales,
        borderColor: "#ff0000",
        backgroundColor: "rgba(255, 0, 0, 0.3)",
      },
      {
        name: "Predicted Sales",
        data: predictedSales,
        borderColor: "#0000ff",
        backgroundColor: "rgba(0, 0, 255, 0.3)",
      },
    ],
    xAxisTitle: "Days",
    yAxisTitle: "Sales",
  };

  useEffect(() => {
    if (
      Object.keys(rawContributionData).length === 0 ||
      rawEffectData.size === 0
    ) {
      return;
    }

    const combined: {
      label: string;
      spend: number;
      effect: number;
    }[] = Object.entries(rawContributionData).map(([label, spend]) => ({
      label,
      spend,
      effect: rawEffectData.get(label) ?? 0,
    }));

    // Sort by effect descending
    combined.sort((a, b) => b.effect - a.effect);

    let displayChannels = combined;
    let otherSpend = 0;
    let otherEffect = 0;

    if (combined.length > 5) {
      displayChannels = combined.slice(0, 5);
      const others = combined.slice(5);
      for (const item of others) {
        otherSpend += item.spend;
        otherEffect += item.effect;
      }
      displayChannels.push({
        label: "Others",
        spend: otherSpend,
        effect: otherEffect,
      });
    }

    setHistoricSpendContributionLabel(
      displayChannels.map(
        (item) => item.label.replace(/spend/i, "").trim() // removes "spend" (case-insensitive) and trims spaces
      )
    );
    setHistoricSpendContributionData(displayChannels.map((item) => item.spend));
    setEffectShareLabel(
      displayChannels.map((item) => item.label.replace(/spend/i, "").trim())
    );
    setEffectShareData(displayChannels.map((item) => item.effect));
  }, [rawContributionData, rawEffectData]);

  return (
    <div className="py-4">
      {isLoading ? (
        <Card className="shadow-md border-0 rounded-lg">
          <div className="flex flex-col items-center justify-center p-8">
            <div className="w-full max-w-md text-center space-y-6">
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{
                      fontSize: 72,
                      color: "#1890ff",
                    }}
                    spin
                  />
                }
                size="large"
              />
              <Title level={3} className="mt-4 text-gray-800">
                AI Analysis in Progress
              </Title>
              <Typography.Paragraph className="text-gray-600">
                Our AI is currently analyzing your marketing data to generate
                insights. This process typically takes a few seconds to
                complete.
              </Typography.Paragraph>
              <div className="mt-4 bg-blue-50 p-4 rounded-lg">
                <Typography.Paragraph className="text-blue-600 mb-0">
                  <InfoCircleOutlined className="mr-2" />
                  Estimated completion time: 3-5 seconds
                </Typography.Paragraph>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <div className="w-full" ref={contentRef}>
          {/* Success Banner */}
          <Card
            className="mb-6 shadow-md border-0 bg-gradient-to-r from-green-50 to-blue-50"
            bodyStyle={{ padding: "16px" }}
          >
            <Row gutter={16} align="middle">
              <Col xs={24} md={16}>
                <div className="flex items-center">
                  <Award className="text-green-500 mr-4" size={32} />
                  <div>
                    <Title level={4} className="m-0 text-gray-800">
                      Analysis Complete
                    </Title>
                    <Typography.Paragraph className="mb-0 text-gray-600">
                      Your marketing mix model analysis has been completed
                      successfully. Review the insights below to optimize your
                      marketing strategy.
                    </Typography.Paragraph>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={8} className="mt-4 md:mt-0">
                <div className="flex justify-end gap-2">
                  {timerActive && (
                    <div className="flex items-center mr-4">
                      <Tooltip title="Page will refresh automatically when timer completes">
                        <div className="flex items-center">
                          <Progress
                            type="circle"
                            percent={timerPercent}
                            width={36}
                            format={() => <ReloadOutlined />}
                            status="active"
                          />
                          <span className="ml-2 text-gray-600">
                            {formatTimeRemaining()}
                          </span>
                          <Button
                            type="text"
                            icon={<ReloadOutlined />}
                            size="small"
                            className="ml-2"
                            onClick={() => window.location.reload()}
                            title="Refresh now"
                          />
                        </div>
                      </Tooltip>
                    </div>
                  )}
                  <Tooltip title="Download Report as PDF">
                    <Button
                      onClick={generatePDF}
                      icon={<FilePdfOutlined />}
                      loading={exportLoading}
                      disabled={true}
                    >
                      Export PDF
                    </Button>
                  </Tooltip>
                  <Tooltip title="Share Results">
                    <Button disabled icon={<ShareAltOutlined />}>
                      Share
                    </Button>
                  </Tooltip>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Model Performance Summary */}
          {modelAccuracy && modelAccuracy[0] && (
            <Card
              className="mb-6 shadow-sm"
              hoverable
              bordered={true}
              style={{ borderTop: "3px solid #143969" }}
            >
              <Title level={4} className="mb-4">
                <LineChartOutlined
                  className="inline-block mr-2"
                  style={{ color: "#143969" }}
                />{" "}
                Model Performance
              </Title>
              <Row gutter={[24, 24]}>
                <Col xs={24} sm={12} md={6}>
                  <Card
                    className="text-center h-full"
                    bordered={false}
                    style={{ background: "#f9f9f9" }}
                  >
                    <Statistic
                      title={
                        <span className="flex items-center justify-center">
                          <CheckCircleOutlined
                            style={{ color: "#3f8600", marginRight: "8px" }}
                          />
                          <span>R-Squared</span>
                        </span>
                      }
                      value={(modelAccuracy[0]["R_Squared"] * 100).toFixed(2)}
                      suffix="%"
                      valueStyle={{ color: "#3f8600", fontSize: "24px" }}
                    />
                    <Progress
                      percent={Math.min(
                        100,
                        Number((modelAccuracy[0]["R_Squared"] * 100).toFixed(0))
                      )}
                      strokeColor="#3f8600"
                      size="small"
                      showInfo={false}
                      style={{ marginTop: "8px", marginBottom: "8px" }}
                    />
                    <Typography.Paragraph className="text-xs text-gray-500 mt-1">
                      Model Accuracy score or its proportion of variance.
                    </Typography.Paragraph>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card
                    className="text-center h-full"
                    bordered={false}
                    style={{ background: "#f9f9f9" }}
                  >
                    <Statistic
                      title={
                        <span className="flex items-center justify-center">
                          <BarChartOutlined
                            style={{ color: "#1890ff", marginRight: "8px" }}
                          />
                          <span>MAPE</span>
                        </span>
                      }
                      value={(modelAccuracy[0]["MAPE"] * 100).toFixed(2)}
                      suffix="%"
                      valueStyle={{ color: "#1890ff", fontSize: "24px" }}
                      prefix={<RiseOutlined style={{ marginRight: "4px" }} />}
                    />
                    <Progress
                      percent={Math.min(
                        100,
                        Number((modelAccuracy[0]["MAPE"] * 100).toFixed(0))
                      )}
                      strokeColor="#1890ff"
                      size="small"
                      showInfo={false}
                      style={{ marginTop: "8px", marginBottom: "8px" }}
                    />
                    <Typography.Paragraph className="text-xs text-gray-500 mt-1">
                      Mean absolute percent error in predictions.
                    </Typography.Paragraph>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card
                    className="text-center h-full"
                    bordered={false}
                    style={{ background: "#f9f9f9" }}
                  >
                    <Statistic
                      title={
                        <span className="flex items-center justify-center">
                          <DollarOutlined
                            style={{ color: "#722ed1", marginRight: "8px" }}
                          />
                          <span>NSpend</span>
                        </span>
                      }
                      value={(modelAccuracy[0]["NSpend"] * 100).toFixed(2)}
                      suffix="%"
                      valueStyle={{ color: "#722ed1", fontSize: "24px" }}
                      prefix={
                        <PieChartOutlined style={{ marginRight: "4px" }} />
                      }
                    />
                    <Progress
                      percent={Math.min(
                        100,
                        Number((modelAccuracy[0]["NSpend"] * 100).toFixed(0))
                      )}
                      strokeColor="#722ed1"
                      size="small"
                      showInfo={false}
                      style={{ marginTop: "8px", marginBottom: "8px" }}
                    />
                    <Typography.Paragraph className="text-xs text-gray-500 mt-1">
                      Normalized spend error or business logic error metric.
                      Measures how well spend-based contributions are captured;
                      vital for budget allocation accuracy.
                    </Typography.Paragraph>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card
                    className="text-center h-full"
                    bordered={false}
                    style={{ background: "#f9f9f9" }}
                  >
                    <Statistic
                      title={
                        <span className="flex items-center justify-center">
                          <LineChartOutlined
                            style={{ color: "#fa8c16", marginRight: "8px" }}
                          />
                          <span>MNRMSE</span>
                        </span>
                      }
                      value={(modelAccuracy[0]["NNRMSE"] * 100).toFixed(2)}
                      suffix="%"
                      valueStyle={{ color: "#fa8c16", fontSize: "24px" }}
                      prefix={
                        <InfoCircleOutlined style={{ marginRight: "4px" }} />
                      }
                    />
                    <Progress
                      percent={Math.min(
                        100,
                        Number((modelAccuracy[0]["NNRMSE"] * 100).toFixed(0))
                      )}
                      strokeColor="#fa8c16"
                      size="small"
                      showInfo={false}
                      style={{ marginTop: "8px", marginBottom: "8px" }}
                    />
                    <Typography.Paragraph className="text-xs text-gray-500 mt-1">
                      Model Normalized Root Mean Square Error over parameter
                      search.
                    </Typography.Paragraph>
                  </Card>
                </Col>
              </Row>
            </Card>
          )}
          {/* Water fall Chart */}

          {/* Seasonality and Trend */}
          {/* {trendDataSet.length > 0 && seasonLabel.length > 0 && (
            <div className="flex justify-between mb-6">
              <div className="w-full mt-6">
                <Maximize title="Seasonality">
                  <div className="overflow-auto">
                    <LineChart
                      labels={seasonLabel}
                      dataSets={seasonDataSet}
                      yAxisTitle="Level Component For Sale"
                      xAxisTitle="Date"
                    />
                  </div>
                </Maximize>
              </div>
            </div>
          )} */}
          {waterFallChartData && waterFallChartLabel && (
            <>
              <div className="w-full">
                <Maximize title="Accumulated Waterfall Chart">
                  <div style={{ height: "750px" }}>
                    <WaterFallChart
                      data={waterFallChartData}
                      labels={waterFallChartLabel}
                    />
                  </div>
                </Maximize>
              </div>
              {/* <div className="w-full">
                    <Maximize title="Accumulated Waterfall Chart (Base 100)">
                      <div style={{ height: "400px" }}>
                        <WaterFallChart
                          data={waterFallChartData}
                          labels={waterFallChartLabel}
                          isBase100={true}
                        />
                      </div>
                    </Maximize>
                  </div> */}
            </>
          )}
          {/* Spend and effect share chart  */}
          {correlationChart && mediaVariables.length > 0 && (
            <div className="space-y-6">
              {/* First row: Spend Share and Effect Share side by side */}
              <div className="flex flex-col gap-6">
                {historicSpendContributionLabel.length > 0 &&
                  historicSpendContributionData.length > 0 &&
                  effectShareData.length > 0 &&
                  effectShareLabel.length > 0 && (
                    <Maximize title="Spend vs. Effect by Channel">
                      <div style={{ height: "450px" }}>
                        <MultiBarLineChartECharts
                          labels={historicSpendContributionLabel} // same as effectShareLabel
                          barDatasets={[
                            {
                              name: "Spend",
                              values: historicSpendContributionData,
                            },
                            {
                              name: "Effect",
                              values: effectShareData,
                            },
                          ]}
                          lineDatasets={[]} // No lines in this chart
                        />
                        <p className="text-sm text-gray-500 mt-2">
                          Spend and Effect are modeled metrics. "Effect"
                          represents the estimated impact of each channel.
                          Channels with lower contributions are grouped into
                          "Others."
                        </p>
                      </div>
                    </Maximize>
                  )}
                {/* Contribution (Club both) */}
                {/* {historicSpendContributionData.length > 0 &&
                  historicSpendContributionLabel.length > 0 && (
                    <div className="flex-1">
                      <Maximize title="Spend Share">
                        <div style={{ height: "400px" }}>
                          <PieChartECharts
                            data={historicSpendContributionData}
                            labels={historicSpendContributionLabel}
                          />
                        </div>
                      </Maximize>
                    </div>
                  )} */}
                {/* Effect Share */}
                {/* {effectShareData.length > 0 && effectShareLabel.length > 0 && (
                  <div className="flex-1">
                    <Maximize title="Effect Share">
                      <div style={{ height: "400px" }}>
                        <PieChartECharts
                          data={effectShareData}
                          labels={effectShareLabel}
                        />
                      </div>
                    </Maximize>
                  </div>
                )} */}
              </div>
              <MediaChannelDashboardRoi
                spendShare={spendShare}
                rawData={roiFinalData}
              />
              {/* Actual vs Predicted - COMMENTED OUT */}
              {twoLineChartData && modelAccuracy && modelAccuracy[0] && (
                <div className="w-full">
                  <Maximize title="Actual Vs Predicted">
                    <div style={{ height: "500px" }}>
                      <div className="flex justify-center">
                        <div className="mx-12">
                          MAPE:{" "}
                          {((modelAccuracy[0]["MAPE"] * 100) as number).toFixed(
                            2
                          )}{" "}
                          %
                        </div>
                        <div className="mr-12">
                          R-Square:{" "}
                          {(
                            (modelAccuracy[0]["R_Squared"] * 100) as number
                          ).toFixed(2)}{" "}
                          %
                        </div>
                      </div>
                      <LineChart {...twoLineChartData} />
                    </div>
                  </Maximize>
                </div>
              )}
              {/* {trendDataSet.length > 0 && seasonLabel.length > 0 && (
                <div className="mt-6 w-full">
                  <Maximize title="Trend">
                    <div className="overflow-auto w-full">
                      <LineChart
                        labels={seasonLabel}
                        dataSets={trendDataSet}
                        xAxisTitle="Date"
                        yAxisTitle="Level Component For Sale"
                      />
                    </div>
                  </Maximize>
                </div>
              )} */}
              {/* Second row: Correlation Chart - COMMENTED OUT
              {correlationChart && (
                <div className="w-full">
                  <Maximize title="Correlation">
                    <div style={{ height: "400px" }}>
                      <div className="flex gap-4 mb-4 px-4">
                        {marketLocations.length > 0 && (
                          <Select
                            className="w-40"
                            value={selectedMarket}
                            onChange={onMarketLocationChange}
                          >
                            {marketLocations.map((location) => (
                              <Option key={location} value={location}>
                                {location.toUpperCase()}
                              </Option>
                            ))}
                          </Select>
                        )}
                        <Select
                          className="w-40"
                          defaultValue={mediaVariables[0]}
                          onChange={onCorrelationMediaVariableChange}
                        >
                          {mediaVariables.map((variable) => (
                            <Option key={variable} value={variable}>
                              {variable.toUpperCase()}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      <LineChart {...correlationChart} />
                    </div>
                  </Maximize>
                </div>
              )} */}

              {/* Correlation Matrix - COMMENTED OUT
              {heatMapData && (
                <div className="w-full">
                  <Maximize title="Correlation Matrix">
                    <HeatMap data={heatMapData} />
                  </Maximize>
                </div>
              )} */}

              {/* Similarity Matrix - COMMENTED OUT
              {similarityHeatMapData && (
                <div className="w-full">
                  <Maximize title="Similarity Matrix">
                    <HeatMap data={similarityHeatMapData} />
                  </Maximize>
                </div>
              )} */}

              {/* Adstock Chart */}
              {(() => {
                // Use actual data if available, otherwise use fallback data
                const displayData = adstockData;

                if (displayData) {
                  return (
                    <div className="w-full">
                      <Maximize title="Adstock Parameters">
                        <div className="flex justify-end items-center mb-2 px-4 py-4">
                          <div>
                            <Button
                              type="primary"
                              icon={<InfoCircleOutlined />}
                              onClick={() =>
                                setShowChartLabels(!showChartLabels)
                              }
                              style={{
                                marginRight: "8px",
                                background: "#143969",
                                borderColor: "#143969",
                              }}
                            >
                              {showChartLabels ? "Hide Labels" : "Show Labels"}
                            </Button>
                          </div>
                        </div>
                        <div style={{ height: "500px" }}>
                          <MultiBarLineChartECharts
                            {...displayData}
                            showLabels={showChartLabels}
                          />
                        </div>
                      </Maximize>
                    </div>
                  );
                }
                return null;
              })()}
              {/* Saturation Curves */}
              <div>
                <h1 className="text-2xl font-bold text-center my-8">
                  Saturation Curves
                </h1>
                {(() => {
                  // Use actual data if available, otherwise use fallback data
                  const displayOptions =
                    saturationChartOptions.length > 0
                      ? saturationChartOptions.filter(
                          (ele) =>
                            !ele.label.toLocaleLowerCase().includes("point")
                        )
                      : generateFallbackSaturationData();

                  // If we have options to display (either real or fallback)
                  if (displayOptions.length > 0) {
                    // Make sure we have a selected option
                    const currentSelection =
                      selectedSaturation || displayOptions[0].key;
                    if (selectedSaturation !== currentSelection) {
                      setSelectedSaturation(currentSelection);
                    }

                    // Find the currently selected option
                    const displayOption =
                      displayOptions.find(
                        (opt) => opt.key === currentSelection
                      ) || displayOptions[0];

                    return (
                      <>
                        <div className="mb-4 flex justify-center">
                          <Select
                            style={{ minWidth: 240 }}
                            value={currentSelection}
                            onChange={setSelectedSaturation}
                            options={displayOptions.map((opt) => ({
                              value: opt.key,
                              label: opt.label,
                            }))}
                          />
                        </div>
                        <div>
                          {displayOption && displayOption.chartProps ? (
                            <div>
                              <Maximize title={displayOption.label}>
                                <LineChart {...displayOption.chartProps} />
                              </Maximize>
                              <div className="mt-4 text-center text-sm text-gray-500">
                                <InfoCircleOutlined className="mr-1" />
                                This chart shows the relationship between
                                spending and response for {displayOption.label}.
                                The curve demonstrates diminishing returns as
                                spending increases.
                                {saturationChartOptions.length === 0 && (
                                  <div className="mt-2 text-amber-500">
                                    <ReloadOutlined className="mr-1" />
                                    Note: Using sample data for visualization.
                                    Actual data will be shown when available.
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center p-8 bg-gray-50 rounded-lg">
                              <Typography.Text type="secondary">
                                <InfoCircleOutlined className="mr-2" />
                                No saturation curve data available for the
                                selected option.
                              </Typography.Text>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  } else {
                    // No data available at all
                    return (
                      <div className="text-center p-8 bg-gray-50 rounded-lg">
                        <Typography.Text type="secondary">
                          <InfoCircleOutlined className="mr-2" />
                          No saturation curve data available. Please check your
                          data source.
                        </Typography.Text>
                      </div>
                    );
                  }
                })()}
              </div>
              {/* YOY Media Contribution
              {mediaYOYSpentChart && (
                <div className="w-full">
                  <Maximize title="YOY Media Contribution">
                    <div className="flex justify-between items-center mb-2 px-4 py-4">
                      <div>
                        <Typography.Text type="secondary">
                          <InfoCircleOutlined className="mr-1" />
                          Year-over-year contribution of each media channel
                        </Typography.Text>
                      </div>
                      <button
                        onClick={() => setShowChartLabels(!showChartLabels)}
                        className="px-3 py-1 text-sm text-[#143969] border border-[#143969] rounded hover:bg-[#143969] hover:text-white transition-colors"
                      >
                        {showChartLabels ? "Hide Labels" : "Show Labels"}
                      </button>
                    </div>
                    <div style={{ height: "500px" }}>
                      <MultiBarLineChartECharts
                        labels={mediaYOYSpentChart.labels}
                        barDatasets={mediaYOYSpentChart.barDatasets}
                        lineDatasets={mediaYOYSpentChart.lineDatasets}
                        showLabels={showChartLabels}
                      />
                    </div>
                  </Maximize>
                </div>
              )} */}

              {/* Elasticity Report */}
              {elasticityColumn.length > 0 && elasticityRecord.length > 0 && (
                <div className="w-full">
                  <Maximize title="Elasticity Report">
                    <div className="w-full px-2">
                      <Table
                        columns={elasticityColumn}
                        records={elasticityRecord}
                      />
                    </div>
                  </Maximize>
                </div>
              )}

              {/* Multi-collinearity Check - COMMENTED OUT
              {multiColinearityRecords.length > 0 && (
                <div className="w-full">
                  <Maximize title="Multi Collinearity Check">
                    <Table
                      columns={multiColinearityColumn}
                      records={multiColinearityRecords}
                    />
                  </Maximize>
                </div>
              )} */}

              {/* Waterfall Charts - COMMENTED OUT
              {waterFallChartData && waterFallChartLabel && (
                <>
                  <div className="w-full">
                    <Maximize title="Accumulated Waterfall Chart">
                      <div style={{ height: "400px" }}>
                        <WaterFallChart
                          data={waterFallChartData}
                          labels={waterFallChartLabel}
                        />
                      </div>
                    </Maximize>
                  </div>
                  <div className="w-full">
                    <Maximize title="Accumulated Waterfall Chart (Base 100)">
                      <div style={{ height: "400px" }}>
                        <WaterFallChart
                          data={waterFallChartData}
                          labels={waterFallChartLabel}
                          isBase100={true}
                        />
                      </div>
                    </Maximize>
                  </div>
                </>
              )} */}

              {/* Sales Decomposition - COMMENTED OUT */}
              {salesDecompositionData.length > 0 &&
                salesDecompositionLabel.length > 0 && (
                  <div className="w-full">
                    <Maximize title="Sales Decomposition">
                      <div style={{ height: "500px" }}>
                        <GradientStackedAreaChart
                          data={salesDecompositionData}
                          labels={salesDecompositionLabel}
                        />
                      </div>
                    </Maximize>
                  </div>
                )}

              {/* Contribution (from Scenario Planner) */}
              {contributionChartData.length > 0 && (
                <div className="w-full">
                  <div className="divider"></div>
                  <h1 className="text-2xl font-bold text-center mt-8 mb-2">
                    Contribution
                  </h1>
                  <div className="w-full mt-2 mb-8">
                    <Maximize title="Contribution Chart">
                      <div style={{ height: "500px" }}>
                        <PieChartECharts
                          data={contributionChartData}
                          labels={contributionChartLabel}
                        />
                      </div>
                    </Maximize>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Chat Component */}
          {/* {jobConfig && (
            <Chat
              jobConfig={jobConfig}
              tenantId={getTenantId()}
              apiEndpoint={MMM_CHAT_API}
            />
          )} */}
        </div>
      )}
    </div>
  );
};

export default ViewResults;
